import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import ProductCard, { ProductActions, PriceLine, ProductTile, NotifyForm } from "@/components/ProductCard";
import { api } from "@/lib/api";
import NotFound from "./NotFound";

const COLLECTIONS = [
  { key: "signature", name: "Signature Collection", line: "The mark of the Chronicles." },
  { key: "truth-that-confronts", name: "Truth That Confronts", line: "The campaign's central charge, worn." },
  { key: "author-collection", name: "The Chronicles — Author Collection", line: "Pieces chosen by Jermaine Miller." },
  { key: "limited", name: "Limited Editions", line: "Numbered runs. Announced first to the reader list." },
];

function useProducts(query = "") {
  const [products, setProducts] = useState(null);
  useEffect(() => {
    api.get(`/public/products${query}`).then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, [query]);
  return products;
}

export default function Merch() {
  const products = useProducts("?type=merch");
  const apparel = (products || []).filter((p) => p.category === "apparel");
  const accessories = (products || []).filter((p) => p.category === "accessories");

  return (
    <div className="bg-ink min-h-screen" data-testid="merch-page">
      <Seo title="Official Merch — Dirty Church Chronicles"
        description="The official merchandise store of Dirty Church Chronicles. Premium apparel, accessories, and limited editions — launching soon."
        path="/merch" />
      <section className="pt-40 pb-20 max-w-7xl mx-auto px-6 sm:px-10">
        <Reveal>
          <p className="eyebrow mb-4">The Official Store</p>
          <h1 className="font-display font-black uppercase text-paper text-4xl sm:text-6xl tracking-tight mb-4" data-testid="merch-title">
            Wear the Word
          </h1>
          <p className="font-reading text-xl text-paper/70 max-w-2xl">
            Premium apparel and goods from the world of Dirty Church Chronicles. Every piece is in final design —
            nothing ships until it earns the name.
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-20">
        <Reveal><p className="eyebrow mb-8">Collections</p></Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {COLLECTIONS.map((c, i) => (
            <Reveal key={c.key} delay={i * 0.08}>
              <div className="border border-paper/10 bg-coal p-8 h-full transition-colors duration-500 hover:border-crimson/50" data-testid={`collection-${c.key}`}>
                <span className="font-display text-crimson text-sm tracking-[0.2em]">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-display text-xl text-paper mt-4 leading-snug">{c.name}</h3>
                <p className="font-ui text-xs text-paper/45 mt-3 leading-relaxed">{c.line}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-20">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <Reveal><h2 className="font-display font-bold text-3xl text-paper">Apparel</h2></Reveal>
          <Link to="/merch/apparel" data-testid="merch-apparel-link"
            className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.22em] text-crimsonhi hover:text-paper transition-colors duration-300">
            All apparel <ArrowRight size={13} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {apparel.map((p) => <Reveal key={p.product_id}><ProductCard product={p} /></Reveal>)}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-28">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <Reveal><h2 className="font-display font-bold text-3xl text-paper">Accessories</h2></Reveal>
          <Link to="/merch/accessories" data-testid="merch-accessories-link"
            className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.22em] text-crimsonhi hover:text-paper transition-colors duration-300">
            All accessories <ArrowRight size={13} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {accessories.map((p) => <Reveal key={p.product_id}><ProductCard product={p} /></Reveal>)}
        </div>
        <Reveal delay={0.15}>
          <p className="font-ui text-sm text-paper/45 mt-14 max-w-xl leading-relaxed border-l-2 border-crimson pl-6">
            Every item launches only after designs, production costs, and fulfillment are approved. Join the
            notification list on any product and you'll hear the moment it opens.
          </p>
        </Reveal>
      </section>
    </div>
  );
}

const CATEGORY_META = {
  apparel: { title: "Apparel", desc: "Heavyweight tees, oversized cuts, hoodies, crewnecks, and embroidered hats." },
  accessories: { title: "Accessories", desc: "Mugs, journals, totes, bookmarks, and limited-edition pieces." },
};

export function MerchCategory() {
  const { category } = useParams();
  const meta = CATEGORY_META[category];
  const products = useProducts(`?type=merch&category=${category}`);
  if (!meta) return <NotFound />;
  return (
    <div className="bg-ink min-h-screen" data-testid={`merch-${category}-page`}>
      <Seo title={`${meta.title} — Official Merch — Dirty Church Chronicles`}
        description={`${meta.desc} Official Dirty Church Chronicles merchandise.`} path={`/merch/${category}`} />
      <section className="pt-40 pb-16 max-w-7xl mx-auto px-6 sm:px-10">
        <Link to="/merch" data-testid="back-to-merch"
          className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.22em] text-paper/50 hover:text-paper transition-colors mb-8">
          <ArrowLeft size={13} /> Official Merch
        </Link>
        <Reveal>
          <h1 className="font-display font-black uppercase text-paper text-4xl sm:text-6xl tracking-tight mb-4">{meta.title}</h1>
          <p className="font-reading text-xl text-paper/70 max-w-2xl">{meta.desc}</p>
        </Reveal>
      </section>
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-28">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {(products || []).map((p) => <Reveal key={p.product_id}><ProductCard product={p} /></Reveal>)}
        </div>
      </section>
    </div>
  );
}

export function MerchProduct() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    api.get(`/public/products/${id}`).then((r) => setP(r.data)).catch(() => setMissing(true));
  }, [id]);
  if (missing) return <NotFound />;
  if (!p) return <div className="bg-ink min-h-screen pt-40 px-6"><p className="font-ui text-paper/40 text-xs uppercase tracking-[0.3em] text-center">Loading…</p></div>;

  return (
    <div className="bg-ink min-h-screen" data-testid="merch-product-page">
      <Seo title={`${p.title} — Official Merch — Dirty Church Chronicles`}
        description={p.description} path={`/merch/product/${p.product_id}`} />
      <section className="pt-36 pb-24 max-w-6xl mx-auto px-6 sm:px-10">
        <Link to="/merch" data-testid="product-back-link"
          className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.22em] text-paper/50 hover:text-paper transition-colors mb-10">
          <ArrowLeft size={13} /> Official Merch
        </Link>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <div className="aspect-[4/5] border border-paper/10 overflow-hidden" data-testid="product-image">
              <ProductTile product={p} />
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="eyebrow mb-4">{p.subtitle}</p>
            <h1 className="font-display font-bold text-3xl sm:text-5xl text-paper leading-tight mb-4" data-testid="product-title">{p.title}</h1>
            {p.badge && (
              <span className="inline-block font-ui text-[10px] uppercase tracking-[0.2em] px-4 py-1.5 rounded-full border border-paper/20 text-paper/50 mb-6">
                {p.badge}
              </span>
            )}
            <p className="font-reading text-xl text-paper/75 leading-relaxed">{p.description}</p>
            {p.specs && (
              <ul className="mt-7 space-y-2.5">
                {p.specs.split("\n").filter(Boolean).map((s, i) => (
                  <li key={i} className="font-ui text-sm text-paper/55 flex gap-3">
                    <span className="text-crimsonhi">✦</span>{s}
                  </li>
                ))}
              </ul>
            )}
            {p.sizes && (
              <div className="mt-7">
                <p className="eyebrow mb-3">Sizes</p>
                <div className="flex gap-2 flex-wrap">
                  {p.sizes.split(",").filter(Boolean).map((s) => (
                    <span key={s} className="font-ui text-xs border border-paper/20 text-paper/60 px-4 py-2 rounded-full">{s}</span>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-9 flex items-center gap-5 flex-wrap">
              <PriceLine product={p} />
              <ProductActions product={p} />
            </div>
            {p.availability === "coming_soon" && (
              <p className="font-ui text-xs text-paper/40 mt-6 max-w-md leading-relaxed">
                This piece launches after designs, production costs, and fulfillment are approved. Print-on-demand
                by our fulfillment partner; books and merch always ship separately.
              </p>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
